import React, { useState, useEffect  } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPhone, faEnvelope, faClock } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { API } from '../config/api';
import './pages.css';

const ContactPage = () => {
  const [storeSettings, setStoreSettings] = useState({
    storeAddress: '123 Food Street, Lagos',
    storePhone: '+234 700 NOUREESH',
    storeEmail: 'hello@noureeshfoods.com',
    openingTime: '9:00 AM',
    closingTime: '9:00 PM',
    openingHours: {
      weekday: '9am - 9pm',
      sunday: '10am - 6pm'
    }
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const formatTime = (timeString) => {
      if (!timeString) return '9:00 AM';
      
      try {
        const [hours, minutes] = timeString.split(':');
        const hourInt = parseInt(hours, 10);
        const period = hourInt >= 12 ? 'PM' : 'AM';
        const hour12 = hourInt % 12 || 12; // Convert 0 or 24 to 12
        return `${hour12}:${minutes || '00'} ${period}`;
      } catch (error) {
        console.error('Error formatting time:', error);
        return '9:00 AM';
      }
    };

    const fetchStoreSettings = async () => {
      try {
  const response = await axios.get(`${API}/settings`);
        if (response.data && response.data.store) {
          const { storeAddress, storePhone, storeEmail, openingTime, closingTime } = response.data.store;
          
          // Format opening hours
          const formattedOpeningHours = {
            weekday: `${formatTime(openingTime || '09:00')} - ${formatTime(closingTime || '21:00')}`,
            sunday: `10:00 AM - 6:00 PM` // Default Sunday hours
          };
          
          setStoreSettings({
            storeAddress: storeAddress || '123 Food Street, Off Culinary Avenue, Lagos',
            storePhone: storePhone || '+234 700 NOUREESH',
            storeEmail: storeEmail || 'hello@noureeshfoods.com',
            openingTime: openingTime ? formatTime(openingTime) : '9:00 AM',
            closingTime: closingTime ? formatTime(closingTime) : '9:00 PM',
            openingHours: formattedOpeningHours
          });
        }
      } catch (error) {
        console.error('Error fetching store settings:', error);
        // Keep default values if API call fails
      } finally {
        setLoading(false);
      }
    };

    fetchStoreSettings();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setTimeout(() => setSubmitted(false), 5000);
  };

  if (loading) {
    return (
      <div className="about-page">
        <div className="text-center py-5">
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero bg-danger text-white py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h1 className="display-4 fw-bold mb-3">Contact Us</h1>
              <p className="lead">
                We'd love to hear from you! Reach out for inquiries, feedback, or just to say hello.
              </p>
            </Col>
            <Col lg={6} className="text-lg-end">
              <Button 
                variant="light" 
                size="lg" 
                href="https://wa.me/2348032417124" 
                target="_blank"
                className="whatsapp-btn"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
                Chat on WhatsApp
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form and Info Section */}
      <section className="py-5">
        <Container>
          <Row>
            {/* Contact Form */}
            <Col lg={6} className="mb-5 mb-lg-0">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <h2 className="mb-4">Send Us a Message</h2>
                  
                  {submitted ? (
                    <div className="alert alert-success">
                      Thank you for your message! We'll get back to you soon.
                    </div>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Name*</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email Address*</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Subject*</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder="Order Inquiry"
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Your Message*</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="How can we help you?"
                        />
                      </Form.Group>

                      <Button variant="danger" type="submit" className="w-100 py-2">
                        Send Message
                      </Button>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Contact Info and Map */}
            <Col lg={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <h2 className="mb-4">Our Information</h2>
                  
                  <div className="contact-info mb-4">
                    <div className="d-flex mb-3">
                      <div className="icon-wrapper bg-danger bg-opacity-10 text-danger me-3">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </div>
                      <div>
                        <h5>Our Location</h5>
                        <p className="mb-0">{storeSettings.storeAddress}</p>
                      </div>
                    </div>

                    <div className="d-flex mb-3">
                      <div className="icon-wrapper bg-danger bg-opacity-10 text-danger me-3">
                        <FontAwesomeIcon icon={faPhone} />
                      </div>
                      <div>
                        <h5>Phone</h5>
                        <p className="mb-0">{storeSettings.storePhone}</p>
                      </div>
                    </div>

                    <div className="d-flex mb-3">
                      <div className="icon-wrapper bg-danger bg-opacity-10 text-danger me-3">
                        <FontAwesomeIcon icon={faEnvelope} />
                      </div>
                      <div>
                        <h5>Email</h5>
                        <p className="mb-0">{storeSettings.storeEmail}</p>
                      </div>
                    </div>

                    <div className="d-flex">
                      <div className="icon-wrapper bg-danger bg-opacity-10 text-danger me-3">
                        <FontAwesomeIcon icon={faClock} />
                      </div>
                      <div>
                        <h5>Opening Hours</h5>
                        <p className="mb-0">Monday - Saturday: {storeSettings.openingHours.weekday}</p>
                        <p className="mb-0">Sunday: {storeSettings.openingHours.sunday}</p>
                      </div>
                    </div>
                  </div>

                  <div className="map-container mt-4">
                    <div className="ratio ratio-16x9">
                      <iframe 
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.123456789012!2d3.1234567890123456!3d6.123456789012345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMDcnMjQuNSJOIDPCsDA3JzI0LjUiRQ!5e0!3m2!1sen!2sng!4v1234567890123!5m2!1sen!2sng" 
                        style={{ border: 0 }}
                        allowFullScreen="" 
                        loading="lazy"
                        title="Noureesh Foods Location"
                      ></iframe>
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <Button 
                      variant="outline-danger" 
                      href="https://wa.me/2348032417124" 
                      target="_blank"
                      className="whatsapp-btn-mobile"
                    >
                      <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
                      WhatsApp Us
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default ContactPage;